/* eslint-disable react/require-default-props */
import { FC, useState, useEffect, ChangeEvent, useRef, KeyboardEvent } from 'react';
import { useRouter } from 'next/router';
import { Grid, InputAdornment, SxProps, Theme } from '@mui/material';

import { InlineTextField } from '@care/react-component-lib';
import { Icon24InfoLocation } from '@care/react-icons';
import {
  validPartialZipCodeRegex,
  validZipCodeRegex,
  isOnlyNumbers,
  isIncompleteZip,
} from '@/utilities/globalValidations';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import { Location } from '@/types/common';
import useZipLocation from './features/zipLocation/useZipLocation';

export interface ZipInputProps {
  location?: Location;
  showLabel?: boolean;
  showLocationIcon?: boolean;
  prepopulateZip?: boolean;
  onChange: (location: Location) => void;
  onError: (error: boolean) => void;
  setValidateOnClick?: (arg: boolean) => void;
  validateOnLostFocusOrClick?: boolean;
  setShouldGoNext?: (arg: boolean) => void;
  validateOnClick?: boolean;
  sx?: SxProps<Theme>;
  errorOverride?: string;
}

const ZipInput: FC<ZipInputProps> = ({
  location,
  showLabel = true,
  showLocationIcon = true,
  validateOnLostFocusOrClick = false,
  validateOnClick = false,
  prepopulateZip = false,
  setValidateOnClick = () => null,
  setShouldGoNext = () => null,
  onChange,
  onError,
  sx,
  errorOverride,
}) => {
  const [shouldValidate, setShouldValidate] = useState(false);
  const textInput = useRef<HTMLInputElement>(null);

  const { zipComponentState, loading, validateZipCode, setZipcodeHook } = useZipLocation({
    initialZipcode: location?.zipcode ?? '',
    validateOnLostFocusOrClick,
    populateZipOnPageEnter: prepopulateZip,
  });

  const { zipcode, city, state, lat, lng, helperText, error } = zipComponentState;
  const { zipcode: zipcodeStingParam } = useRouter().query;

  const blockNonNumericInput = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isOnlyNumbers(event.key)) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (zipcodeStingParam && !zipcode) {
      setZipcodeHook(zipcodeStingParam as string);
    } else if (isIncompleteZip(zipcode)) {
      setShouldValidate(true);
      if (textInput && textInput.current) {
        textInput.current.onfocus = () => setShouldValidate(false);
      }
    }
  }, []);

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShouldGoNext(false);
    setValidateOnClick(false);
    const newZipcode = e.target.value;

    if (!validPartialZipCodeRegex.test(newZipcode)) {
      return;
    }
    setZipcodeHook(newZipcode);
  };

  useEffect(() => {
    validateZipCode(validateOnLostFocusOrClick);
  }, [zipcode]);

  useEffect(() => {
    onError(error || loading);
  }, [error, loading]);

  useEffect(() => {
    onChange({ zipcode, city, state, lat, lng });
  }, [zipcode, city, state]);

  useEffect(() => {
    if (!validateOnLostFocusOrClick) {
      return;
    }
    if (textInput && textInput.current) {
      textInput.current.onblur = () => setShouldValidate(true);
      textInput.current.onfocus = () => setShouldValidate(false);
    }
  }, []);

  const showValidationResult = (): { message: string; validationError: boolean } => {
    const isLoading = () => loading && { message: '', validationError: false };

    const defaultValidation = () =>
      !validateOnLostFocusOrClick && {
        message: zipcode ? helperText : '',
        validationError: Boolean(error && zipcode),
      };

    const validateOnCompleteZipcode = () =>
      validZipCodeRegex.test(zipcode) && { message: helperText, validationError: error };

    const validateOnButtonClick = () =>
      validateOnClick && { message: helperText, validationError: error };

    const onLostFocusValidation = () => ({
      message: shouldValidate ? helperText : '',
      validationError: shouldValidate && error,
    });

    const shouldValidateZip = () =>
      shouldValidate &&
      isIncompleteZip(zipcode) && {
        message: zipcode ? helperText : '',
        validationError: Boolean(error && zipcode),
      };

    return (
      isLoading() ||
      defaultValidation() ||
      validateOnCompleteZipcode() ||
      validateOnButtonClick() ||
      onLostFocusValidation() ||
      shouldValidateZip()
    );
  };

  const { message, validationError } = showValidationResult();

  useEffect(() => {
    if ((validationError || errorOverride) && message && (shouldValidate || validateOnClick)) {
      AnalyticsHelper.logEvent({
        name: 'Error Viewed',
        data: {
          enrollment_step: 'Care Location',
          error_description: 'Enter a valid ZIP code (e.g. "02453")',
          screen_name: 'Enrollment',
        },
      });
    }
  }, [validationError, errorOverride, message]);

  return (
    <Grid item xs={12} sx={sx}>
      <InlineTextField
        inputRef={textInput}
        id="zipCode"
        name="zipCode"
        label={showLabel && 'ZIP code'}
        helperText={message || errorOverride}
        error={Boolean(validationError || errorOverride)}
        value={zipcode}
        onChange={onInputChange}
        onKeyPress={blockNonNumericInput}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {showLocationIcon && <Icon24InfoLocation />}
            </InputAdornment>
          ),
          inputProps: { inputMode: 'numeric', pattern: '[0-9]*' },
        }}
        sx={{
          '& .MuiInputBase-root': {
            ...(!showLabel && { height: '46px', paddingTop: '10px' }),
          },
        }}
      />
    </Grid>
  );
};

export default ZipInput;
